import {action, makeAutoObservable} from "mobx"
import SyncOrdersApi, {StateAndActionGroupedPair} from "../../api/SyncOrdersApi"
import {CommandData} from "../../utils/RequestsHelper"


class SyncOrdersController {
    public readonly productSyncByOptions = [
        {label: "ID артикула", value: "sku_id"},
        {label: "ID товара", value: "product_id"},
        {label: "Код артикула", value: "sku_code"}
    ]

    private _defaultValue: { label: string; value: string } = {label:"", value:"0"}

    constructor() {
        makeAutoObservable(this)
    }

    private _addPostingNumber2comment!: boolean

    get addPostingNumber2comment(): boolean {
        return this._addPostingNumber2comment
    }

    set addPostingNumber2comment(value: boolean) {
        SyncOrdersApi.setAddPostingNumber2comment(value)
        this._addPostingNumber2comment = value
    }

    private _addSendDate2comment!: boolean

    get addSendDate2comment(): boolean {
        return this._addSendDate2comment
    }

    set addSendDate2comment(value: boolean) {
        SyncOrdersApi.setAddSendDateTime2comment(value)
        this._addSendDate2comment = value
    }

    private _isMultiOrder!: boolean
    private _pickShippingDate!: boolean
    private _singleExpressCustomer!: boolean
    private _fioInComment!: boolean
    private _noOrderIfEmptyItem!: boolean
    private _ozonCustomerId!: string

    get isMultiOrder(): boolean {
        return this._isMultiOrder
    }

    set isMultiOrder(value: boolean) {
        SyncOrdersApi.setIsMultiOrder(value)
        this._isMultiOrder = value
    }

    get pickShippingDate(): boolean {
        return this._pickShippingDate
    }

    set pickShippingDate(value: boolean) {
        SyncOrdersApi.setPickShippingDate(value)
        this._pickShippingDate = value
    }

    set noOrderIfEmptyItem(value: boolean) {
        SyncOrdersApi.setNoOrderIfEmptyItem(value)
        this._noOrderIfEmptyItem = value
    }

    get noOrderIfEmptyItem(): boolean {
        return this._noOrderIfEmptyItem
    }

    get singleExpressCustomer(): boolean {
        return this._singleExpressCustomer
    }

    set singleExpressCustomer(value: boolean) {
        SyncOrdersApi.setSingleExpressCustomer(value)
        this._singleExpressCustomer = value
    }

    get fioInComment(): boolean {
        return this._fioInComment
    }

    set fioInComment(value: boolean) {
        SyncOrdersApi.setFioInComment(value)
        this._fioInComment = value
    }

    get ozonCustomerId(): string {
        return this._ozonCustomerId
    }

    set ozonCustomerId(value: string) {
        SyncOrdersApi.setOzonCustomerId(value)
        this._ozonCustomerId = value
    }

    private _defaultShippingMethod: string = ""

    get defaultShippingMethod(): string {
        return this._defaultShippingMethod
    }

    set defaultShippingMethod(value: string) {
        SyncOrdersApi.setDefaultShippingMethod(value)
        this._defaultShippingMethod = value
    }

    private _defaultPaymentMethod: string = ""

    get defaultPaymentMethod(): string {
        return this._defaultPaymentMethod
    }

    set defaultPaymentMethod(value: string) {
        SyncOrdersApi.setDefaultPaymentMethod(value)
        this._defaultPaymentMethod = value
    }

    private _isDebugState: boolean = false

    get isDebugState(): boolean {
        return this._isDebugState
    }

    set isDebugState(value: boolean) {
        SyncOrdersApi.setIsDebugState(value)
        this._isDebugState = value
    }

    private _syncOrdersCommand?: CommandData

    get syncOrdersCommand(): CommandData | undefined {
        return this._syncOrdersCommand
    }

    private _shop_states?: { label: string; value: string }[]

    get shop_states(): { label: string; value: string }[] | undefined {
        return this._shop_states
    }


    private _shippingMethods: { label: string; value: string }[] = [this._defaultValue]

    get shippingMethods(): { label: string; value: string }[]  {
        return this._shippingMethods
    }

    private _paymentMethods: { label: string; value: string }[] = [this._defaultValue]

    get paymentMethods(): { label: string; value: string }[] {
        return this._paymentMethods
    }

    private _stateAndActionGroupedPairs?: StateAndActionGroupedPair[]

    get stateAndActionGroupedPairs(): StateAndActionGroupedPair[] | undefined {
        return this._stateAndActionGroupedPairs
    }

    get isStatesLoaded(): boolean {
        return !!this._shop_states && !!this._stateAndActionGroupedPairs
    }

    private _since_date: Date = new Date()

    get since_date(): Date {
        return this._since_date
    }

    set since_date(value: Date) {
        this._since_date = value
        SyncOrdersApi.setSinceDate(value.getTime())
    }

    private _sync_by: string = "sku_id"

    get sync_by(): string {
        return this._sync_by
    }

    set sync_by(value: string) {
        this._sync_by = value
        SyncOrdersApi.setProductSyncBy(value)
    }

    getGroupOfActionByModel(model: string): StateAndActionGroupedPair {
        if (!this._stateAndActionGroupedPairs) throw new Error(`not loaded states and actions`)
        for (const stateAndActionGroupedPair of this._stateAndActionGroupedPairs) {
            if (stateAndActionGroupedPair.name === model) return stateAndActionGroupedPair
        }
        throw new Error(`${model} not found`)
    }

    loadSettings() {
        this._stateAndActionGroupedPairs = undefined
        this._shop_states = undefined
        this._shippingMethods = [this._defaultValue]
        this._paymentMethods = [this._defaultValue]
        SyncOrdersApi.getSettings().then(action("ordersSettingsLoaded", (response) => {
            const data = response.data.data
            this._sync_by = data.sync_by
            this._since_date = new Date(data.since_time)
            this._stateAndActionGroupedPairs = data.stateAndActionGroupedPairs
            this._shop_states = data.shopActions
            this._syncOrdersCommand = data.syncOrdersCommand
            this._isDebugState = data.isDebugState
            this._addPostingNumber2comment = data.addPostingNumber2comment
            this._addSendDate2comment = data.addSendDateTime2comment
            this._defaultShippingMethod = data.defaultShippingMethod
            this._defaultPaymentMethod = data.defaultPaymentMethod
            this._isMultiOrder = data.isMultiOrder
            this._ozonCustomerId = data.ozonCustomerId
            this._singleExpressCustomer = data.singleExpressCustomer
            this._fioInComment = data.fioInComment
            this._noOrderIfEmptyItem = data.noOrderIfEmptyItem
            this._shippingMethods.push(...data.shippingMethods)
            this._paymentMethods.push(...data.paymentMethods)
        }))
    }
}

export const syncOrdersController = new SyncOrdersController()